import { atom } from 'jotai';

import { AutoSaveState } from '@/shared-project-builder-v2-core';

import { IntegrationUrlField } from './shared-manager-project-integration-url.helpers';
import { Project } from './shared-manager-project.types';

export const TAB_VALUES = {
  FORM: 'form',
  SETTINGS: 'settings',
  ALL_RESPONSES: 'responses',
  ANALYTICS: 'analytics',
} as const;

export const LEGACY_THEMES_TAB_VALUE = 'themes';
export const LEGACY_CLIPS_TAB_VALUE = 'clips';

export const PLAN_CODE_FREE = 'FREE';

export const CONTACT_INFORMATION_TAG_NAME = 'internal:contact';

export const previewAtom = atom<string | null>(null);

export const publishAtom = atom<Project | null>(null);

export const showAutoSaveState = atom<AutoSaveState>('idle');

export const flushAutoSaveAtom = atom<number>(0);

export const flushAutoSaveHandledAtom = atom<number>(0);

export interface FlushAutoSaveError {
  nonce: number;
  error: string;
}

export const flushAutoSaveErrorAtom = atom<FlushAutoSaveError | null>(null);

export const deletingQuestionIdsAtom = atom<ReadonlySet<string>>(
  new Set<string>()
);

export type ProjectCreatePhase = 'idle' | 'creating' | 'created';

export const projectCreatePhaseAtom = atom<ProjectCreatePhase>('idle');

export const pendingQuestionCreatesAtom = atom<
  ReadonlyMap<string, Promise<string | null>>
>(new Map());

export const creatingQuestionIdsAtom = atom<ReadonlySet<string>>(
  (get) => new Set(get(pendingQuestionCreatesAtom).keys())
);

/**
 * A pending request to open the AI Companion conversation panel beside the current
 * page, scoped to `project`. This channel exists because `@/manager-companion`
 * imports the project libs, so they cannot import it back to open it directly —
 * they set a request here and the companion's dock host consumes it. `nonce` makes
 * an identical repeat request a new one.
 */
export interface CompanionDockRequest {
  project: { id: string; label: string };
  /** Auto-sent first turn; omit to just open the panel for the user to type in. */
  prompt?: string;
  nonce: number;
}

export const companionDockRequestAtom = atom<CompanionDockRequest | null>(null);

/** Bumped after the companion rewrote a project, so its open project page remounts on the fresh copy. */
export const projectPageReloadAtom = atom({ projectId: '', token: 0 });

export const bumpProjectPageReloadAtom = atom(
  null,
  (get, set, projectId: string) => {
    set(projectPageReloadAtom, {
      projectId,
      token: get(projectPageReloadAtom).token + 1,
    });
  }
);

export const lastPersistedIntegrationUrlsAtom = atom<
  Partial<Record<IntegrationUrlField, string | null | undefined>>
>({});

export const IMPORT_TIMEOUT_IN_MS = 120_000; // 2 minutes

export const IMPORT_MAX_FILE_SIZE = 1024 * 1024; // 1MB

// Maximum value for 32-bit signed integer (GraphQL Int type limit)
export const MAX_INT_32 = 2147483647;

export const IMPORT_ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/rtf',
  'text/rtf',
];
